import type { JobCardDetailDto } from '@artic/api-client';
import type { QueryClient } from '@tanstack/react-query';

/** The Orval response envelope (`{ status, data }`) the job-card hook resolves to. */
interface JobCardDetailResponse {
  status: number;
  data: JobCardDetailDto;
}

/**
 * Job Card display name from the cached detail response, for the breadcrumb.
 *
 * There is no `job_card_name` column: Legacy titles the page with
 * `job_card_operation_name ?? operation_versions.operation_name`, which the
 * endpoint already coalesces into `operation_name`. Returning `null` while the
 * cache is cold omits the crumb rather than showing the raw UUID — which is what
 * Legacy's breadcrumb fell back to.
 *
 * Matched on the key's URL **suffix** rather than rebuilt with
 * `getJobCardsPartnerControllerGetJobCardQueryKey`: that key needs the partner id,
 * and a breadcrumb resolver only receives the route match and the QueryClient.
 * A job card id is globally unique, so the suffix identifies exactly one query.
 *
 * Reads the cache directly — breadcrumb resolvers run outside React and must not
 * fetch. The detail page's own query is what populates it.
 */
export function readJobCardName(queryClient: QueryClient, jobCardId: string): string | null {
  const suffix = `/job-cards/${jobCardId}`;
  const matches = queryClient.getQueriesData<JobCardDetailResponse>({
    predicate: (query) => {
      const [url] = query.queryKey;
      return typeof url === 'string' && url.endsWith(suffix);
    },
  });

  for (const [, response] of matches) {
    if (response?.status === 200) return response.data.operation_name;
  }
  return null;
}
